import { Controller } from "@hotwired/stimulus"

// The three bindings people arrive already knowing — e archives, s snoozes, # trashes — and Escape to
// drop a selection. Front's and Gmail's, deliberately: a shortcut nobody has to learn is the only kind
// worth shipping for an act that is already one hover away. a / t / p open the rail's typeaheads
// (assign, type, priority) the same way Linear does, by clicking the trigger already on the page.
//
// It performs nothing itself. Each key resolves to a button that is already on the page and clicks it,
// the way list_controller advances the pane by clicking a row's own link. So the write, the label that
// decides which direction it goes, the list params stamped onto it and the toast that comes back are all
// the ones the mouse gets. A shortcut that posted for itself would be a second write path to keep in
// step with four partials — and `s` in particular has to open the duration menu rather than pick a wake
// time on the reader's behalf, which only the real button knows how to do.
const ACTION_KEYS = { e: "archive", s: "snooze", "#": "trash", Escape: "clear-selection" }
const FIELD_KEYS = { a: "assign", t: "request-type", p: "priority" }
const OPEN_BULK_BAR = ".list__bulk-bar:not([hidden])"
const CONVERSATION_ROW = ".conversation-row"
const THREAD_RAIL = ".thread-info"
// Every field a character means something in, which is not every input: a row's own checkbox is where
// focus lands the moment somebody ticks one, and a tickbox has no use for the letter e.
const TEXT_FIELDS = "textarea, select, input:not([type='checkbox']):not([type='radio'])"

export default class extends Controller {
  press(event) {
    const action = ACTION_KEYS[event.key] ?? FIELD_KEYS[event.key]
    if (!action || this.typing(event) || this.aMenuIsOpen) return

    const button = this.buttonFor(action)
    if (!button) return

    event.preventDefault()
    button.click()
  }

  // A selection outranks everything else: the bar is on screen naming what the key is about to move, so
  // acting on one focused row instead would move something other than what the reader is looking at.
  // Then the row the keyboard is actually on. Then the thread in the pane, because "e" with no row
  // focused is about what you are reading.
  buttonFor(action) {
    const selector = `[data-conversation-action="${action}"]`

    return document.querySelector(`${OPEN_BULK_BAR} ${selector}`) ??
           this.focusedRow?.querySelector(selector) ??
           document.querySelector(`${THREAD_RAIL} ${selector}`) ??
           null
  }

  get focusedRow() {
    return document.activeElement?.closest?.(CONVERSATION_ROW) ?? null
  }

  // The composer is a contenteditable and the search box is an input, and in both of those a bare "e"
  // has to be the letter e — this is the classic way the feature ships broken. Modifier combinations
  // are left alone for the same reason from the other side: ⌘E belongs to the browser, not to us.
  // Shift is not among them, because "#" is a shifted key on most layouts.
  typing(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) return true
    // A panel resize handle owns its keys — Escape cancels a drag there, not the selection.
    if (event.target?.closest?.("[role='separator']")) return true

    return !!(event.target?.isContentEditable || event.target?.closest?.(TEXT_FIELDS))
  }

  // Escape belongs to whatever is open before it belongs to the selection: pressing it with the snooze
  // durations showing has to close them, not silently clear what the next pick was going to move. The
  // letters are held back for the same moment — focus is inside the menu, so they are that menu's keys.
  // A modal dialog is the same kind of open: `e` with the duplicate picker up must not archive
  // the thread behind it the moment focus is on a candidate rather than in the search box.
  // `:not([hidden])` is load-bearing for the command palette, which stays in the DOM as a
  // closed dialog — without it every letter key would be dead the moment the overlay mounts.
  get aMenuIsOpen() {
    return !!document.querySelector("[role='menu']:not([hidden]), [role='dialog'][aria-modal='true']:not([hidden])")
  }
}
